/**
 * WHAT THE STATE COLUMN OF AN AUTOMATIC NOTIFICATION SAYS: WHERE ITS SWITCH STANDS, AND WHAT
 * ACTUALLY REACHES A PLAYER.
 *
 * It said "Sending" beside every switched-on row, then (the first fix) judged delivery by channel,
 * so "Account restriction applied" read a green "On" for an email nothing sends and "KYC outcome"
 * would have read "Off" while its email still went out (module walk and its review, 30 Oct 2026).
 *
 * NOTHING READS THESE SWITCHES. The only reader of the settings is this screen's own pair of routes
 * (services/api backoffice/platform.ts); no mailer call, job or player route consults them. So until
 * the owner decides whether the switches should work or go, each row says where its switch stands
 * and what really happens, from the list below — found in the code, not read off the Channels
 * column:
 *
 *   kyc   the identity result is emailed (IDENTITY_RESULT: the reviewer's decision in
 *         player-identity/routes.ts, the service's in worker settle-identity-checks.ts, one more try
 *         in retry-grant.ts) and a decided check is shown in the player app's notification list
 *         (player-notifications/routes.ts)
 *   wdl   a released or rejected withdrawal is shown in that list; no email on the decision itself (a payout returned after release is emailed), nothing pushed
 *   promo, risk, digest — nothing: no mail template, no list entry, no push
 *
 * A key not listed is treated as nothing sending it, the claim that cannot overstate.
 */
const SENT_TODAY: Readonly<Record<string, string>> = {
  kyc: 'emailed and shown in the player app',
  wdl: 'shown in the player app',
};

export interface NotificationState {
  tone: 'ok' | 'quiet' | 'attention';
  /** The status dot's label, read by a screen reader. */
  label: string;
  /** The words beside the dot. */
  words: string;
}

const NOT_READ = 'this switch is not read yet';

export function notificationState(row: { key: string; enabled: boolean; mandatory: boolean }): NotificationState {
  const sent = Object.hasOwn(SENT_TODAY, row.key) ? SENT_TODAY[row.key] : undefined;
  if (sent !== undefined)
    return row.enabled
      ? { tone: 'ok', label: 'Switched on; sent', words: `On — ${sent}; ${NOT_READ}` }
      : { tone: 'attention', label: 'Switched off; sent anyway', words: `Off — ${sent} anyway; ${NOT_READ}` };
  return row.enabled
    ? {
        /* A notice the licence requires, which nothing sends, needs a look. */
        tone: row.mandatory ? 'attention' : 'quiet',
        label: 'Switched on; nothing sends this yet',
        words: 'On — nothing sends this yet',
      }
    : { tone: 'quiet', label: 'Switched off; nothing sends this yet', words: 'Off — nothing sends this yet' };
}

/** What the switch dialog says a move does: it is recorded, and changes nothing a player receives. */
export function switchConsequence(key: string, turningOn: boolean): string {
  const sent = Object.hasOwn(SENT_TODAY, key) ? SENT_TODAY[key] : undefined;
  return (
    `Switching it ${turningOn ? 'on' : 'off'} records that, with your reason, against the setting and in ` +
    'the audit trail. Nothing reads these switches yet, so it changes nothing a player receives: ' +
    (sent !== undefined ? `this notice is ${sent} either way.` : 'nothing sends this notice, on or off.')
  );
}

/** The confirmation after a move: what was recorded, not a change in what players receive. */
export function switchedToast(label: string, enabled: boolean): string {
  return `“${label}” is recorded as ${enabled ? 'on' : 'off'}. Nothing reads this switch yet, so what players receive is unchanged.`;
}
